import { useEffect, useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Check, X, Users, UserPlus } from "lucide-react";
import { format } from "date-fns";

interface QueueEntry {
  id: string;
  user_id: string;
  guest_name: string | null;
  joined_at: string;
  status: 'waiting' | 'admitted' | 'denied';
}

interface Profile {
  display_name: string;
}

interface WaitingRoomQueueProps {
  meetingId: string;
}

export default function WaitingRoomQueue({ meetingId }: WaitingRoomQueueProps) {
  const [queue, setQueue] = useState<QueueEntry[]>([]);
  const [profiles, setProfiles] = useState<Map<string, Profile>>(new Map());
  const { toast } = useToast();

  useEffect(() => {
    fetchQueue();

    // Subscribe to queue updates
    const channel = supabase
      .channel(`waiting-room-queue-${meetingId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'waiting_room_queue',
          filter: `meeting_id=eq.${meetingId}`
        },
        () => {
          fetchQueue();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [meetingId]);

  const fetchQueue = async () => {
    const { data: queueData, error } = await supabase
      .from('waiting_room_queue')
      .select('*')
      .eq('meeting_id', meetingId)
      .eq('status', 'waiting')
      .order('joined_at', { ascending: true });

    if (error) {
      console.error('Error fetching queue:', error);
      return;
    }

    // Type assertion since we know the data structure matches
    setQueue((queueData || []) as QueueEntry[]);

    // Fetch profiles for users in queue
    const userIds = queueData?.map(q => q.user_id).filter(Boolean) || [];
    if (userIds.length > 0) {
      const { data: profileData } = await supabase
        .from('profiles')
        .select('user_id, display_name')
        .in('user_id', userIds);

      const profileMap = new Map(
        profileData?.map(p => [p.user_id, { display_name: p.display_name }]) || []
      );
      setProfiles(profileMap);
    }
  };

  const handleAdmit = async (queueId: string, userId: string) => {
    const { error } = await supabase
      .from('waiting_room_queue')
      .update({
        status: 'admitted',
        responded_at: new Date().toISOString(),
        responded_by: (await supabase.auth.getUser()).data.user?.id
      })
      .eq('id', queueId);

    if (error) {
      toast({
        title: "Error",
        description: "Failed to admit participant",
        variant: "destructive"
      });
      return;
    }

    toast({
      title: "Participant admitted",
      description: "User has been allowed to join the meeting"
    });
  };

  const handleDeny = async (queueId: string) => {
    const { error } = await supabase
      .from('waiting_room_queue')
      .update({
        status: 'denied',
        responded_at: new Date().toISOString(),
        responded_by: (await supabase.auth.getUser()).data.user?.id
      })
      .eq('id', queueId);

    if (error) {
      toast({
        title: "Error",
        description: "Failed to deny participant",
        variant: "destructive"
      });
      return;
    }

    toast({
      title: "Participant denied",
      description: "User has been denied access to the meeting"
    });
  };

  const handleAdmitAll = async () => {
    const { error } = await supabase
      .from('waiting_room_queue')
      .update({
        status: 'admitted',
        responded_at: new Date().toISOString(),
        responded_by: (await supabase.auth.getUser()).data.user?.id
      })
      .eq('meeting_id', meetingId)
      .eq('status', 'waiting');

    if (error) {
      toast({
        title: "Error",
        description: "Failed to admit all participants",
        variant: "destructive"
      });
      return;
    }

    toast({
      title: "All participants admitted",
      description: `${queue.length} user(s) have been allowed to join`
    });
  };

  if (queue.length === 0) {
    return null;
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Users className="h-5 w-5" />
              Waiting Room
              <Badge variant="secondary">{queue.length}</Badge>
            </CardTitle>
            <CardDescription>Participants waiting to join</CardDescription>
          </div>
          {queue.length > 1 && (
            <Button onClick={handleAdmitAll} size="sm">
              <UserPlus className="h-4 w-4 mr-2" />
              Admit All
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent>
        <div className="space-y-3">
          {queue.map((entry) => {
            const profile = profiles.get(entry.user_id);
            const displayName = entry.guest_name || profile?.display_name || 'Anonymous';
            const initials = displayName.substring(0, 2).toUpperCase();

            return (
              <div
                key={entry.id}
                className="flex items-center justify-between p-3 border border-border rounded-lg hover:bg-muted/50 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <Avatar className="h-10 w-10">
                    <AvatarFallback>{initials}</AvatarFallback>
                  </Avatar>
                  <div>
                    <p className="font-medium">{displayName}</p>
                    <p className="text-xs text-muted-foreground">
                      Waiting since {format(new Date(entry.joined_at), 'HH:mm')}
                    </p>
                  </div>
                </div>
                
                <div className="flex items-center gap-2">
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => handleAdmit(entry.id, entry.user_id)}
                    className="text-green-600 hover:text-green-700 hover:bg-green-50"
                  >
                    <Check className="h-4 w-4" />
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => handleDeny(entry.id)}
                    className="text-red-600 hover:text-red-700 hover:bg-red-50"
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}
